// AI-generated. Reviewed by: —
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { acceptInvitation } from '@/services/invitationService';

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'idle' | 'accepting' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get('token');

  // Auto-accept as soon as the page loads with a valid token
  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    setStatus('accepting');

    acceptInvitation(token)
      .then(() => {
        if (cancelled) return;
        setStatus('done');
        // Give the user a moment to see the success message, then send to login
        setTimeout(() => navigate('/login', { replace: true }), 2000);
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('error');
        setError(
          'This invitation link is invalid, has already been used, or has expired. ' +
          'Please ask your administrator to send a new invitation.',
        );
      });

    return () => { cancelled = true; };
  }, [token, navigate]);

  if (!token) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-brand-surface">
        <div className="bg-white rounded-lg shadow p-8 max-w-sm w-full text-center">
          <h1 className="text-xl font-semibold mb-2">Invalid Link</h1>
          <p className="text-brand-text-secondary text-sm">
            This invitation link is missing a token. Please use the link from your invitation email.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-brand-surface">
      <div className="bg-white rounded-lg shadow p-8 max-w-sm w-full text-center">
        {status === 'accepting' && (
          <>
            <h1 className="text-xl font-semibold mb-2">Accepting Invitation…</h1>
            <p className="text-brand-text-secondary text-sm">Please wait a moment.</p>
          </>
        )}

        {status === 'done' && (
          <>
            <h1 className="text-xl font-semibold mb-2">Invitation Accepted!</h1>
            <p className="text-brand-text-secondary text-sm">
              Your account is ready. Redirecting you to sign in…
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <h1 className="text-xl font-semibold mb-2">Unable to Accept Invitation</h1>
            <p className="text-red-500 text-sm" role="alert">{error}</p>
          </>
        )}
      </div>
    </div>
  );
}
